import React, { useEffect, useState } from "react";
import { api, authHeaders, errMessage, setToken } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, KeyRound, Ban, Trash2, Copy, Star, Users, Tag, Ticket, PiggyBank, UserPlus, LogIn, Cake, Heart, Globe } from "lucide-react";
import { toast } from "sonner";

const Stat = ({ label, value, icon: Icon }) => (
  <div className="rounded-xl bg-slate-50 p-3">
    <div className="flex items-center gap-1.5 text-slate-400 text-xs font-semibold">{Icon && <Icon className="h-3.5 w-3.5" />} {label}</div>
    <p className="text-xl font-extrabold text-slate-900 mt-0.5">{value}</p>
  </div>
);

const GENDER_LABELS = { uomo: "Uomo", donna: "Donna", altro: "Altro", na: "Preferisco non dirlo" };

const ageOf = (e) => {
  if (!e) return null;
  if (e.birth_date) {
    const b = new Date(e.birth_date);
    if (!isNaN(b)) {
      const now = new Date();
      let a = now.getFullYear() - b.getFullYear();
      if (now.getMonth() < b.getMonth() || (now.getMonth() === b.getMonth() && now.getDate() < b.getDate())) a--;
      return a;
    }
  }
  return e.birth_year ? new Date().getFullYear() - e.birth_year : null;
};

const fmtDate = (iso) => { if (!iso) return "—"; const d = new Date(iso); return isNaN(d) ? "—" : d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); };

export default function AdminUserDetail({ type, id, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newPw, setNewPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [lastPw, setLastPw] = useState("");
  const [impersonating, setImpersonating] = useState(false);
  const [catMap, setCatMap] = useState({});

  useEffect(() => {
    if (type === "client") {
      api.get("/categories").then((r) => {
        const macros = r.data.categories || r.data || [];
        const m = {};
        macros.forEach((mac) => (mac.tags || []).forEach((t) => { m[t.id] = mac.label; }));
        setCatMap(m);
      }).catch(() => {});
    }
  }, [type]);

  const base = type === "vendor" ? "vendors" : "clients";
  const load = () => api.get(`/admin/${base}/${id}`, { headers: authHeaders("admin") })
    .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, type]);

  const entity = data && (type === "vendor" ? data.vendor : data.client);
  const stats = data?.stats || {};
  const interestLabels = React.useMemo(() => {
    if (!entity?.interests?.length) return [];
    const set = new Set();
    entity.interests.forEach((tid) => { const l = catMap[tid]; if (l) set.add(l); });
    return set.size ? [...set] : entity.interests;
  }, [entity, catMap]);

  const resetPw = async () => {
    const val = newPw.trim();
    if (val.length < 6) return toast.error("Almeno 6 caratteri");
    setSavingPw(true);
    try {
      await api.post(`/admin/${base}/${id}/reset-password`, { new_password: val }, { headers: authHeaders("admin") });
      setLastPw(val); setNewPw("");
      toast.success("Password aggiornata");
    } catch (err) { toast.error(errMessage(err)); }
    finally { setSavingPw(false); }
  };
  const toggleBlock = async () => {
    await api.post(`/admin/${base}/${id}/block`, { blocked: !entity.blocked }, { headers: authHeaders("admin") });
    toast.success(!entity.blocked ? "Utente bloccato" : "Utente sbloccato");
    onChanged && onChanged(); load();
  };
  const remove = async () => {
    if (!window.confirm("Eliminare definitivamente questo utente?")) return;
    await api.delete(`/admin/${base}/${id}`, { headers: authHeaders("admin") });
    toast.success("Eliminato"); onChanged && onChanged(); onClose();
  };
  const copyCode = () => { navigator.clipboard?.writeText(entity.public_code || ""); toast.success("Codice copiato"); };

  const impersonate = async () => {
    setImpersonating(true);
    try {
      const r = await api.post(`/admin/vendors/${id}/impersonate`, {}, { headers: authHeaders("admin") });
      setToken("vendor", r.data.token);
      const w = window.open(r.data.vendor?.store_ready ? "/vendor" : "/vendor/store", "_blank");
      if (!w) { window.location.href = r.data.vendor?.store_ready ? "/vendor" : "/vendor/store"; }
      toast.success("Sei entrato come negozio (nuova scheda)");
    } catch (err) { toast.error(errMessage(err)); }
    finally { setImpersonating(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg rounded-3xl max-h-[92vh] overflow-y-auto" data-testid="admin-user-detail">
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#E63946]" /></div>
        ) : !entity ? (
          <p className="py-10 text-center text-slate-400">Utente non trovato</p>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-extrabold flex items-center gap-2">
                {type === "vendor" ? entity.store_name || entity.email : entity.name || entity.email}
                {entity.blocked && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-600">Bloccato</span>}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <button onClick={copyCode} data-testid="detail-code" className="inline-flex items-center gap-1.5 text-sm font-bold px-3 py-1 rounded-full bg-[#FFE4E6] text-[#BE123C]">
                {entity.public_code || "—"} <Copy className="h-3.5 w-3.5" />
              </button>

              <div className="text-sm text-slate-600 space-y-1">
                <p><span className="text-slate-400">Email:</span> <b>{entity.email}</b></p>
                {entity.phone && <p><span className="text-slate-400">Telefono:</span> <b>{entity.phone}</b></p>}
                {type === "vendor"
                  ? ((entity.city || entity.address) && <p><span className="text-slate-400">Luogo:</span> <b>{[entity.address, entity.city].filter(Boolean).join(", ")}</b></p>)
                  : (entity.city && <p><span className="text-slate-400">Città:</span> <b>{entity.city}</b></p>)}
                {type === "client" && entity.birth_date && <p><span className="text-slate-400">Data di nascita:</span> <b>{fmtDate(entity.birth_date)}</b>{ageOf(entity) != null && <span className="text-slate-500"> · {ageOf(entity)} anni</span>}</p>}
                {type === "client" && !entity.birth_date && entity.birth_year && <p><span className="text-slate-400">Anno di nascita:</span> <b>{entity.birth_year}</b>{ageOf(entity) != null && <span className="text-slate-500"> · {ageOf(entity)} anni</span>}</p>}
                {type === "client" && entity.gender && <p><span className="text-slate-400">Genere:</span> <b>{GENDER_LABELS[entity.gender] || entity.gender}</b></p>}
                {type === "client" && entity.country && <p><span className="text-slate-400">Nazione:</span> <b>{entity.country}</b></p>}
                <p><span className="text-slate-400">Registrato:</span> <b>{fmtDate(entity.created_at)}</b></p>
                <p><span className="text-slate-400">Ultimo accesso:</span> <b>{fmtDate(entity.last_login)}</b></p>
                <p><span className="text-slate-400">Accesso:</span> <b>{entity.auth_provider}</b></p>
                {entity.referred_by && <p><span className="text-slate-400">Invitato da:</span> <b>{entity.referred_by.code} ({entity.referred_by.type})</b></p>}
              </div>

              {type === "client" && interestLabels.length > 0 && (
                <div data-testid="detail-interests">
                  <p className="text-xs font-semibold text-slate-400 mb-1.5 flex items-center gap-1"><Heart className="h-3.5 w-3.5" /> Preferenze / Interessi</p>
                  <div className="flex flex-wrap gap-1.5">
                    {interestLabels.map((l, i) => <span key={i} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#FFE4E6] text-[#BE123C]">{l}</span>)}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-3 gap-2">
                {type === "vendor" ? (
                  <>
                    <Stat label="Offerte" value={stats.offers_total ?? 0} icon={Tag} />
                    <Stat label="Attive" value={stats.offers_active ?? 0} icon={Tag} />
                    <Stat label="Follower" value={stats.followers ?? 0} icon={Users} />
                    <Stat label="Coupon" value={stats.claims_total ?? 0} icon={Ticket} />
                    <Stat label="Riscattati" value={stats.claims_redeemed ?? 0} icon={Ticket} />
                    <Stat label="Incasso €" value={stats.revenue_eur ?? 0} icon={PiggyBank} />
                    <Stat label="Recensioni" value={stats.review_count ?? 0} icon={Star} />
                    <Stat label="Media ★" value={stats.review_avg ?? "—"} icon={Star} />
                    <Stat label="Ha invitato" value={(stats.invited_vendors || 0) + (stats.invited_clients || 0)} icon={UserPlus} />
                  </>
                ) : (
                  <>
                    <Stat label="Coupon" value={stats.claims_total ?? 0} icon={Ticket} />
                    <Stat label="Usati" value={stats.claims_redeemed ?? 0} icon={Ticket} />
                    <Stat label="Risparmio €" value={stats.total_saved ?? 0} icon={PiggyBank} />
                    <Stat label="Speso €" value={stats.total_spent ?? 0} icon={PiggyBank} />
                    <Stat label="Salvati" value={stats.saved_offers ?? 0} icon={Tag} />
                    <Stat label="Segue" value={stats.following ?? 0} icon={Users} />
                    <Stat label="Ha invitato" value={(stats.invited_vendors || 0) + (stats.invited_clients || 0)} icon={UserPlus} />
                  </>
                )}
              </div>

              {(stats.invited_vendors > 0 || stats.invited_clients > 0) && (
                <p className="text-xs text-slate-500">Inviti: <b>{stats.invited_vendors}</b> venditori · <b>{stats.invited_clients}</b> clienti</p>
              )}

              {/* Password management */}
              <div className="rounded-xl border border-slate-200 p-3 space-y-2">
                <p className="text-sm font-bold text-slate-700 flex items-center gap-1.5"><KeyRound className="h-4 w-4 text-[#E63946]" /> Gestione password</p>
                <p className="text-xs text-slate-400">Le password sono criptate e non leggibili. Puoi impostarne una nuova.</p>
                <div className="flex gap-2">
                  <Input value={newPw} onChange={(e) => setNewPw(e.target.value)} autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder="Nuova password (min 6)" className="h-10 rounded-xl" data-testid="detail-new-password" />
                  <Button onClick={resetPw} disabled={savingPw} data-testid="detail-set-password" className="h-10 rounded-xl font-bold">
                    {savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : "Imposta"}
                  </Button>
                </div>
                {lastPw && <p className="text-xs font-bold text-emerald-600" data-testid="detail-last-password">Password impostata: {lastPw}</p>}
              </div>

              {type === "vendor" && (
                <Button onClick={impersonate} disabled={impersonating} data-testid="detail-impersonate" className="w-full h-11 rounded-xl font-bold kn-gradient text-white">
                  {impersonating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogIn className="h-4 w-4 mr-1.5" /> Entra come Negozio</>}
                </Button>
              )}

              {/* Lists (drill-down) */}
              {type === "vendor" && data.offers?.length > 0 && (
                <details className="rounded-xl bg-slate-50 p-3" data-testid="detail-offers">
                  <summary className="text-sm font-bold text-slate-700 cursor-pointer">Offerte ({data.offers.length})</summary>
                  <div className="mt-2 space-y-1">{data.offers.slice(0, 30).map((o) => <p key={o.offer_id} className="text-sm text-slate-600 truncate">• {o.title}</p>)}</div>
                </details>
              )}
              {type === "client" && data.claimed?.length > 0 && (
                <details className="rounded-xl bg-slate-50 p-3" data-testid="detail-claims">
                  <summary className="text-sm font-bold text-slate-700 cursor-pointer">Coupon richiesti ({data.claimed.length})</summary>
                  <div className="mt-2 space-y-1">{data.claimed.slice(0, 30).map((c, i) => <p key={i} className="text-sm text-slate-600 truncate">• {c.title || "—"} · {c.store_name}</p>)}</div>
                </details>
              )}

              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={toggleBlock} data-testid="detail-block" className="flex-1 h-11 rounded-xl font-bold">
                  <Ban className="h-4 w-4 mr-1" /> {entity.blocked ? "Sblocca" : "Blocca"}
                </Button>
                <Button variant="outline" onClick={remove} data-testid="detail-delete" className="h-11 rounded-xl font-bold text-red-600 border-red-200 hover:bg-red-50">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
